/**
 * Save system: 3 local slots, export/import envelopes, migrations.
 * SaveGame contract (contracts/sim.ts): { version, seed, tick, slots: 3, state }.
 * Envelopes are JSON text so a save can be exported as a file and imported on
 * another device; the sim state inside is the canonical binary payload, base64.
 */
import type { SimEngine } from '../sim/core/engine';
import { STATE_VERSION } from '../sim/core/engine';

export const SAVE_VERSION = 0;
export const SLOT_COUNT = 3;
const SLOT_PREFIX = 'exotics-adventure.save.';

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class LocalStorageAdapter implements StorageAdapter {
  constructor(private readonly storage?: StorageAdapter) {}
  private store(): StorageAdapter {
    if (this.storage) return this.storage;
    const g = (globalThis as Record<string, unknown>).localStorage;
    if (!g) throw new Error('save: no localStorage in this context');
    return g as StorageAdapter;
  }
  getItem(k: string): string | null { return this.store().getItem(k); }
  setItem(k: string, v: string): void { this.store().setItem(k, v); }
  removeItem(k: string): void { this.store().removeItem(k); }
}

export class MemoryStorageAdapter implements StorageAdapter {
  private map = new Map<string, string>();
  getItem(k: string): string | null { return this.map.get(k) ?? null; }
  setItem(k: string, v: string): void { this.map.set(k, v); }
  removeItem(k: string): void { this.map.delete(k); }
}

/** The export/import envelope. `state` is base64 of the canonical state bytes. */
export interface SaveEnvelope {
  game: 'exotics-adventure';
  saveVersion: number;
  stateVersion: number;
  seed: number;
  tick: number;
  slots: 3;
  state: string; // base64
  /** Wall-clock save time (ms). Optional: envelopes written before the
   *  offline policy (P128) do not carry it; resume then applies no catch-up. */
  savedAtMs?: number;
}

/** Slot summary for the load screen. `corrupt` entries failed validation;
 *  `backup` says a one-deep backup exists (P129). */
export type SlotInfo =
  | { slot: number; seed: number; tick: number; saveVersion: number; savedAtMs?: number; backup: boolean; corrupt?: false }
  | { slot: number; backup: boolean; corrupt: true }
  | null;

type Migration = (envelope: SaveEnvelope) => SaveEnvelope;

/** Migrations keyed by the saveVersion they upgrade FROM. None yet (v0). */
const MIGRATIONS = new Map<number, Migration>();

export class SaveManager {
  constructor(private readonly storage: StorageAdapter) {}

  private slotKey(slot: number): string {
    if (!Number.isInteger(slot) || slot < 0 || slot >= SLOT_COUNT) {
      throw new Error(`save: slot must be 0-${String(SLOT_COUNT - 1)}, got ${String(slot)}`);
    }
    return `${SLOT_PREFIX}${String(slot)}`;
  }

  private backupKey(slot: number): string {
    return `${this.slotKey(slot)}.bak`;
  }

  /** Overwrite a slot, keeping the previous envelope as a one-deep backup. */
  private writeWithBackup(slot: number, envelopeJson: string): void {
    const live = this.storage.getItem(this.slotKey(slot));
    if (live !== null) this.storage.setItem(this.backupKey(slot), live);
    this.storage.setItem(this.slotKey(slot), envelopeJson);
  }

  saveToSlot(slot: number, engine: SimEngine, savedAtMs?: number): void {
    const envelope = this.buildEnvelope(engine, savedAtMs);
    this.writeWithBackup(slot, JSON.stringify(envelope));
  }

  loadFromSlot(slot: number, engine: SimEngine): void {
    const raw = this.storage.getItem(this.slotKey(slot));
    if (raw === null) throw new Error(`save: slot ${String(slot)} is empty`);
    this.applyEnvelope(raw, engine);
  }

  deleteSlot(slot: number): void {
    this.storage.removeItem(this.slotKey(slot));
    this.storage.removeItem(this.backupKey(slot));
  }

  /** Promote a slot's one-deep backup to live (P129). Validates first: a
   *  corrupt backup is never promoted. The backup stays in place. */
  restoreSlotBackup(slot: number): void {
    const raw = this.storage.getItem(this.backupKey(slot));
    if (raw === null) throw new Error(`save: slot ${String(slot)} has no backup`);
    this.parseAndMigrate(raw); // never promote anything we could not load
    this.storage.setItem(this.slotKey(slot), raw);
  }

  /** Raw envelope text for a slot (null when empty) - used by the worker bridge. */
  readSlotRaw(slot: number): string | null {
    return this.storage.getItem(this.slotKey(slot));
  }

  /** Store a validated envelope built elsewhere (e.g. inside the sim worker). */
  writeSlotRaw(slot: number, envelopeJson: string): void {
    this.parseAndMigrate(envelopeJson); // never store anything we could not load
    this.writeWithBackup(slot, envelopeJson);
  }

  /** Store only when the bytes differ from the live slot (used by the
   *  unload-path flush so a no-op flush does not rotate the backup). */
  writeSlotRawIfChanged(slot: number, envelopeJson: string): boolean {
    if (this.storage.getItem(this.slotKey(slot)) === envelopeJson) return false;
    this.writeSlotRaw(slot, envelopeJson);
    return true;
  }

  /** Slot summaries for the load screen (null = empty). A corrupt primary
   *  no longer throws the whole list: it is flagged so the UI can offer the
   *  backup instead of wedging the load screen (P129). */
  listSlots(): SlotInfo[] {
    const out: SlotInfo[] = [];
    for (let slot = 0; slot < SLOT_COUNT; slot++) {
      const backup = this.storage.getItem(this.backupKey(slot)) !== null;
      const raw = this.storage.getItem(this.slotKey(slot));
      if (raw === null) { out.push(null); continue; }
      try {
        const env = this.parseAndMigrate(raw);
        out.push({
          slot, seed: env.seed, tick: env.tick, saveVersion: env.saveVersion, backup,
          ...(env.savedAtMs !== undefined ? { savedAtMs: env.savedAtMs } : {}),
        });
      } catch {
        out.push({ slot, backup, corrupt: true });
      }
    }
    return out;
  }

  /** Export the live state as portable JSON text, validated against the slot. */
  exportSlot(slot: number, engine: SimEngine, savedAtMs?: number): string {
    this.slotKey(slot); // range-check the slot; the envelope itself is slot-agnostic
    return JSON.stringify(this.buildEnvelope(engine, savedAtMs));
  }

  /** Return the envelope with savedAtMs set (main thread stamps on store;
   *  wall-clock is banned inside the sim worker by the frozen contract). */
  stampEnvelope(envelopeJson: string, savedAtMs: number): string {
    const env = this.parseAndMigrate(envelopeJson);
    return JSON.stringify({ ...env, savedAtMs });
  }

  /** Read an envelope's header without applying it (offline catch-up, P128). */
  inspectEnvelope(envelopeJson: string): { seed: number; tick: number; saveVersion: number; savedAtMs?: number } {
    const env = this.parseAndMigrate(envelopeJson);
    return {
      seed: env.seed, tick: env.tick, saveVersion: env.saveVersion,
      ...(env.savedAtMs !== undefined ? { savedAtMs: env.savedAtMs } : {}),
    };
  }

  importEnvelope(envelopeJson: string, engine: SimEngine): void {
    this.applyEnvelope(envelopeJson, engine);
  }

  private buildEnvelope(engine: SimEngine, savedAtMs?: number): SaveEnvelope {
    return {
      game: 'exotics-adventure',
      saveVersion: SAVE_VERSION,
      stateVersion: STATE_VERSION,
      seed: engine.seed,
      tick: engine.clock.tick,
      slots: 3,
      state: base64Encode(engine.serializeState()),
      ...(savedAtMs !== undefined ? { savedAtMs } : {}),
    };
  }

  private applyEnvelope(envelopeJson: string, engine: SimEngine): void {
    const env = this.parseAndMigrate(envelopeJson);
    if (env.seed !== engine.seed) {
      throw new Error(`save: seed mismatch (save ${String(env.seed)}, game ${String(engine.seed)})`);
    }
    engine.loadInto(base64Decode(env.state));
  }

  private parseAndMigrate(json: string): SaveEnvelope {
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      throw new Error('save: envelope is not valid JSON');
    }
    const env = parsed as Partial<SaveEnvelope>;
    if (env.game !== 'exotics-adventure') throw new Error('save: not an Exotics Adventure save');
    if (typeof env.saveVersion !== 'number') throw new Error('save: missing saveVersion');
    if (env.saveVersion > SAVE_VERSION) {
      throw new Error(`save: save is from a newer version (${String(env.saveVersion)} > ${String(SAVE_VERSION)})`);
    }
    let migrated = env as SaveEnvelope;
    while (migrated.saveVersion < SAVE_VERSION) {
      const step = MIGRATIONS.get(migrated.saveVersion);
      if (!step) throw new Error(`save: no migration from version ${String(migrated.saveVersion)}`);
      migrated = step(migrated);
    }
    if (typeof migrated.state !== 'string') throw new Error('save: missing state payload');
    if (migrated.savedAtMs !== undefined && typeof migrated.savedAtMs !== 'number') {
      throw new Error('save: savedAtMs must be a number');
    }
    return migrated;
  }
}

/* Base64 without btoa/atob (works in node, workers, browsers identically). */
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export function base64Encode(bytes: Uint8Array): string {
  const c = (v: number): string => {
    const ch = B64[v];
    if (ch === undefined) throw new Error('save: base64 table lookup failed');
    return ch;
  };
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] ?? 0;
    const b1 = i + 1 < bytes.length ? (bytes[i + 1] ?? 0) : 0;
    const b2 = i + 2 < bytes.length ? (bytes[i + 2] ?? 0) : 0;
    out += c(b0 >> 2) + c(((b0 & 3) << 4) | (b1 >> 4));
    out += i + 1 < bytes.length ? c(((b1 & 15) << 2) | (b2 >> 6)) : '=';
    out += i + 2 < bytes.length ? c(b2 & 63) : '=';
  }
  return out;
}

export function base64Decode(s: string): Uint8Array {
  const clean = s.replace(/=+$/, '');
  const out: number[] = [];
  let acc = 0;
  let bits = 0;
  for (const ch of clean) {
    const v = B64.indexOf(ch);
    if (v < 0) throw new Error('save: bad base64 in state payload');
    acc = ((acc << 6) | v) & 0x3ffff; // keep only live bits (bits <= 13 after add)
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((acc >> bits) & 0xff);
    }
  }
  return Uint8Array.from(out);
}
